import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Tv,
  Globe,
  Play,
  ArrowRight,
  Zap,
  BarChart3,
  Building,
  AudioWaveform,
  HeadphonesIcon,
  ChevronRight,
  Shield,
  Pause,
  Clock,
  Award,
  Star,
  CheckCircle2,
  Sparkles,
  TrendingUp,
  Users,
  Video,
  Layout,
  Copy,
  Wand2,
} from "lucide-react";
import BackToTop from "../../components/BackToTop/BackToTop";
import TestimonialCarousel, { testimonials } from "../../components/TestimonialCarousel";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import HeroBg from "../../../src/images/hero-bg.png";
import AuthPopup from "../../components/popup/AuthPopup";

export default function Home() {
  const videoRef = useRef(null);
  const [searchParams] = useSearchParams();

  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  useEffect(() => {
    const page = searchParams.get("p");

    if (page === "signup") {
      setAuthMode("signup");
      setShowAuth(true);
    }

    if (page === "login") {
      setAuthMode("login");
      setShowAuth(true);
    }
  }, [searchParams]);


  useEffect(() => {
    const els = document.querySelectorAll(".reveal-text");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.2 }
    );

    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, []);

  useEffect(() => {
    let timeout;
    if (isPlaying) {
      timeout = setTimeout(() => setShowControls(false), 2000);
    }
    return () => clearTimeout(timeout);
  }, [isPlaying, showControls]);

  const handleToggle = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setShowControls(true);
    } else {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setShowControls(true);
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(progress);
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = percentage * videoRef.current.duration;
      setProgress(percentage * 100);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      q: "What is a VAST tag and why do I need it?",
      a: "VAST (Video Ad Serving Template) is a universal format that allows video ads to be served across different platforms and devices. Your VAST tag is like a URL that tells ad servers where to find your video ad and how to display it. With SocialTV, we generate this automatically for you."
    },
    {
      q: "Can I use content from any social media platform?",
      a: "Yes! SocialTV supports imports from Instagram, YouTube, Facebook, X (Twitter), Pinterest, and Vimeo. Simply paste the URL and our AI will optimize it for TV display."
    },
    {
      q: "How long does it take to create a CTV ad?",
      a: "Most users can create a professional CTV ad in under 5 minutes. Simply choose a template, paste your social media URL, and we handle the rest. Advanced customizations may take a bit longer."
    },
    {
      q: "What platforms can I run my ads on?",
      a: "Your VAST tag works with all major CTV platforms including Roku, Amazon Fire TV, Apple TV, Samsung TV Plus, LG Channels, and any ad server that supports VAST 4.2 (Google Ad Manager, The Trade Desk, etc.)."
    },
    {
      q: "Do I need video editing experience?",
      a: "Not at all! Our AI-powered platform handles all the technical aspects. You just need your social media content and we'll transform it into a stunning TV ad automatically."
    },
    {
      q: "Is there a free trial?",
      a: "Yes! Our free plan lets you create up to 3 videos per month. No credit card required. When you're ready for more, you can upgrade to Pro for unlimited videos and 4K quality."
    }
  ];

  const stats = [
    { number: "98%", label: "Client Satisfaction", icon: Star, color: "from-amber-500 to-orange-500" },
    { number: "50M+", label: "Ads Delivered", icon: TrendingUp, color: "from-blue-500 to-cyan-500" },
    { number: "150+", label: "Enterprise Clients", icon: Building, color: "from-purple-500 to-pink-500" },
    { number: "24/7", label: "Support Available", icon: HeadphonesIcon, color: "from-green-500 to-emerald-500" },
  ];

  const steps = [
    {
      number: "01",
      title: "Choose Template",
      description: "Select from 10+ professional CTV ad templates optimized for TV screens and VAST compatibility.",
      icon: Layout,
      tags: ["Modern", "Corporate", "Dynamic"],
      color: "from-indigo-500 to-blue-600"
    },
    {
      number: "02",
      title: "Add Your Content",
      description: "Paste your video URL from YouTube, Facebook, or social media. Add your brand logo and messaging.",
      icon: Wand2,
      features: ["Auto 4K", "HDR", "60fps"],
      color: "from-purple-500 to-pink-600"
    },
    {
      number: "03",
      title: "Get VAST Tag",
      description: "Generate your VAST tag URL instantly. Copy and paste into any ad server or CTV platform.",
      icon: Copy,
      badges: ["Ready in 30s", "VAST 4.2"],
      color: "from-pink-500 to-rose-600"
    },
  ];

  const whyCards = [
    {
      icon: Tv,
      title: "Reach Big Screens",
      description: "Transform your social creatives into stunning TV ads that captivate audiences on the biggest screens.",
      stat: "2.5B+ Connected TVs",
      features: ['4K Ultra HD Quality', 'HDR10+ Support', 'Multi-Device Sync', 'Smart TV Optimized'],
      gradient: "from-blue-500 to-indigo-600",
      bgGradient: "from-blue-50 to-indigo-50",
      statBg: "bg-blue-50 text-blue-600",
      linkColor: "text-blue-600",
      borderColor: "border-blue-100"
    },
    {
      icon: Zap,
      title: "Fast Production",
      description: "Turn any social post into professional CTV ads in minutes, not days. AI handles the heavy lifting.",
      stat: "85% Faster Production",
      features: ['AI-Powered Editing', 'Auto-Formatting', 'Batch Processing', 'Template Library'],
      gradient: "from-orange-500 to-red-600",
      bgGradient: "from-orange-50 to-red-50",
      statBg: "bg-orange-50 text-orange-600",
      linkColor: "text-orange-600",
      borderColor: "border-orange-100"
    },
    {
      icon: BarChart3,
      title: "Measure Performance",
      description: "Track real-time views, engagement metrics, and ROI across all your CTV campaigns.",
      stat: "Real-time Analytics",
      features: ['Live Dashboard', 'Engagement Tracking', 'ROI Calculator', 'Audience Insights'],
      gradient: "from-green-500 to-emerald-600",
      bgGradient: "from-green-50 to-emerald-50",
      statBg: "bg-green-50 text-green-600",
      linkColor: "text-green-600",
      borderColor: "border-green-100"
    },
  ];

  return (
    <div className="bg-white">
      <BackToTop />
      <Header loginClick={() => { setShowAuth(true) }} />

      {/* ================= HERO SECTION ================= */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src={HeroBg}
            alt=""
            className="w-full h-full object-cover opacity-5"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/50" />

          {/* Animated gradient orbs */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob" />
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000" />
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000" />
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 z-0 opacity-[0.03]">
          <div className="h-full w-full" style={{ backgroundImage: 'radial-gradient(circle, #4F46E5 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column */}
            <div className="text-center lg:text-left space-y-8">
              {/* Badge */}
              <div className="flex justify-center lg:justify-start">
                <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                  </span>
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-indigo-600 transition-colors">
                    🚀 New: CTV Launchpad is here
                  </span>
                  <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                    Live now
                  </span>
                </div>
              </div>

              {/* Heading */}
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight">
                Transform Social Posts into
                <span className="block mt-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent animate-gradient">
                  Stunning TV Ads
                </span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Unlock your content from{" "}
                <span className="font-semibold text-indigo-600">Instagram, YouTube, and Facebook</span>
                {" "}with AI-powered platform that creates premium CTV experiences in minutes.
              </p>

              {/* CTA Buttons */}

              <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-4">
                <div onClick={() => setShowAuth(true)}
                  className="group cursor-pointer relative inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-indigo-500/25 transition-all duration-300 hover:-translate-y-0.5 overflow-hidden"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Start Creating Free
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-700 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>

                <Link
                  to="/templates"
                  className="inline-flex items-center gap-2 bg-white border-2 border-gray-200 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:border-indigo-300 hover:bg-indigo-50/30 transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  <Play className="w-5 h-5" />
                  Watch Demo
                </Link>
              </div>

              {/* Trust indicators */}
              {/* <div className="flex items-center gap-6 justify-center lg:justify-start pt-4 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  No credit card required
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Free 3 videos/month
                </div>
              </div> */}
            </div>

            {/* Right Column - Video Player */}
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-3xl blur-2xl opacity-20 animate-pulse" />

              <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-2 shadow-2xl">
                <div className="relative rounded-xl overflow-hidden bg-black">
                  <video
                    ref={videoRef}
                    src="https://test-pages.b-cdn.net/SocialTV/Master/layout3.mp4"
                    className="w-full h-full object-cover aspect-video"
                    playsInline
                    preload="auto"
                    loop
                    autoPlay
                    muted
                    onLoadedData={() => setIsVideoLoaded(true)}
                  />

                  {/* Loading state */}
                  {!isVideoLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                      <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}

                  {/* Overlay badge */}
                  <div className="absolute top-3 right-3 z-10">
                    <span className="px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-lg text-xs text-white font-medium">
                      ⚡ Live Demo
                    </span>
                  </div>
                </div>
              </div>

              {/* Floating cards */}
              <FloatingCard
                className="-bottom-6 -right-6"
                icon={<Zap className="w-5 h-5 text-white" />}
                iconBg="from-green-400 to-emerald-500"
                title="Processing Time"
                value="~30 seconds"
              />

              <FloatingCard
                className="-bottom-4 -left-4"
                icon={<Shield className="w-5 h-5 text-white" />}
                iconBg="from-indigo-400 to-purple-500"
                title="Uptime"
                value="99.9%"
                variant="inline"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= DEMO SECTION ================= */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50/50 to-white" />

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge icon={<Sparkles className="w-3 h-3" />} text="Interactive Demo" />

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mt-6 mb-6">
              See the
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent"> Magic </span>
              Happen
            </h2>

            <p className="text-xl text-gray-600 leading-relaxed">
              Watch how your social media content transforms into a stunning
              <span className="font-semibold text-indigo-600"> Connected TV experience</span> in real-time.
            </p>
          </div>

          {/* Video Card */}
          <div className="max-w-5xl mx-auto">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur-lg opacity-25 group-hover:opacity-50 transition duration-500" />

              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
                {/* Header bar */}
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                    <div className="w-px h-5 bg-gray-300" />
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-500 rounded flex items-center justify-center">
                        <Video className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-700">SocialTV Studio</span>
                    </div>
                  </div>
                  <span className="text-xs px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-full font-medium">4K • HDR</span>
                </div>

                {/* Video */}
                <div
                  className="relative aspect-video bg-black"
                  onMouseEnter={() => isPlaying && setShowControls(true)}
                  onMouseMove={() => isPlaying && setShowControls(true)}
                  onMouseLeave={() => isPlaying && setShowControls(false)}
                >
                  <video
                    src="https://test-pages.b-cdn.net/SocialTV/Master/layout5.mp4"
                    className="w-full h-full object-cover"
                    playsInline
                    preload="metadata"
                    onClick={handleToggle}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={(e) => setDuration(e.target.duration)}
                    onEnded={() => {
                      setIsPlaying(false);
                      setShowControls(true);
                    }}
                  />

                  {/* Play button overlay */}
                  {(showControls || !isPlaying) && (
                    <button
                      onClick={handleToggle}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white/95 backdrop-blur rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300 z-10"
                    >
                      {isPlaying ? (
                        <Pause className="w-6 h-6 text-gray-800" />
                      ) : (
                        <Play className="w-6 h-6 ml-1 text-gray-800" />
                      )}
                    </button>
                  )}

                  {/* Controls */}
                  {showControls && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-8 pb-4 px-4">
                      <div className="flex justify-between text-xs text-white/80 mb-1.5">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                      </div>
                      <div
                        className="h-1 bg-white/30 rounded-full cursor-pointer group/progress"
                        onClick={handleSeek}
                      >
                        <div
                          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full relative transition-all duration-100"
                          style={{ width: `${progress}%` }}
                        >
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover/progress:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-5 bg-white">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex -space-x-2">
                        <PlatformIcon color="from-pink-500 to-purple-600" />
                        <PlatformIcon color="from-blue-500 to-cyan-500" />
                        <PlatformIcon color="from-gray-900 to-gray-700" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Instagram → YouTube → Facebook → CTV</p>
                        <p className="text-xs text-gray-500">Seamless transformation in under 30 seconds</p>
                      </div>
                    </div>

                    <div
                      onClick={() => setShowAuth(true)}
                      className="cursor-pointer inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                    >
                      Try Now
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHY SECTION ================= */}
      <section className="relative py-24 lg:py-32 bg-gray-50/50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-20">
            <Badge icon={<Star className="w-3 h-3" />} text="Why Choose Us" />

            <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-4">
              Why{" "}
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Social CTV
              </span>
            </h2>

            <p className="text-xl text-gray-600 leading-relaxed">
              The smarter way to advertise on connected TV
            </p>
          </div>

          {/* Cards Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {whyCards.map((card, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${card.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                <div className="relative p-8">
                  {/* Icon */}
                  <div className={`w-14 h-14 bg-gradient-to-br ${card.gradient} rounded-xl flex items-center justify-center text-white shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <card.icon className="w-7 h-7" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{card.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{card.description}</p>

                  {/* Stat */}
                  <div className={`inline-flex items-center gap-2 ${card.statBg} px-3 py-1.5 rounded-full text-sm font-medium mb-6`}>
                    <span className="w-1.5 h-1.5 bg-current rounded-full" />
                    {card.stat}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3">
                    {card.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Link */}
                  <div className={`mt-8 pt-4 border-t ${card.borderColor}`}>
                    <a href="#" className={`${card.linkColor} font-medium inline-flex items-center gap-2 group/link`}>
                      Learn more
                      <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Stats Grid */}
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-6 text-center border border-gray-200 hover:border-gray-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${stat.color} mb-4`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stat.number}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center mt-16">
              <div
                onClick={() => setShowAuth(true)}
                className="cursor-pointer group inline-flex items-center gap-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-indigo-500/25 transition-all duration-300 hover:-translate-y-0.5"
              >
                <span>Start your free trial</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </div>
              <p className="mt-4 text-sm text-gray-500">No credit card required • Cancel anytime</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-20">
            <Badge icon={<Sparkles className="w-3 h-3" />} text="Simple Process" />

            <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-4">
              How It{" "}
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Works
              </span>
            </h2>

            <p className="text-xl text-gray-600 leading-relaxed">
              Transform your social content into stunning CTV ads in just three simple steps
            </p>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="group relative">
                <div className="relative bg-white rounded-2xl p-8 border border-gray-200 hover:border-gray-300 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                  {/* Step number */}
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${step.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <step.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-4xl font-bold text-gray-200">{step.number}</span>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{step.description}</p>

                  {/* Tags/Badges */}
                  <div className="flex flex-wrap gap-2">
                    {step.tags?.map((tag, i) => (
                      <span key={i} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">
                        {tag}
                      </span>
                    ))}
                    {step.features?.map((feature, i) => (
                      <span key={i} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        {feature}
                      </span>
                    ))}
                    {step.badges?.map((badge, i) => (
                      <span key={i} className="px-3 py-1.5 bg-green-50 text-green-700 text-xs font-medium rounded-lg">
                        {badge}
                      </span>
                    ))}
                  </div>

                  {/* Connector line (between steps) */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-gray-300 to-gray-200" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="relative py-24 lg:py-32 bg-gray-50/50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge icon={<Users className="w-3 h-3" />} text="Testimonials" />

            <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-4">
              What Our{" "}
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Creators
              </span>{" "}
              Say
            </h2>

            <p className="text-xl text-gray-600 leading-relaxed">
              Join thousands of marketers and creators who transform their social content into stunning CTV ads
            </p>
          </div>

          <TestimonialCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge icon={<Sparkles className="w-3 h-3" />} text="Got Questions?" />

            <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-4">
              Frequently Asked{" "}
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Questions
              </span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-gray-200 hover:border-gray-300 transition-all duration-300 overflow-hidden"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.q}</h3>
                  <ChevronRight
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${activeFaq === index ? 'rotate-90' : ''
                      }`}
                  />
                </button>

                <div
                  className={`px-6 overflow-hidden transition-all duration-300 ${activeFaq === index ? 'pb-5 max-h-96' : 'max-h-0'
                    }`}
                >
                  <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA SECTION ================= */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Content?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
            Join thousands of creators who are already turning their social media posts into stunning TV ads. Start for free today.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <div
              onClick={() => setShowAuth(true)}
              className="cursor-pointer inline-flex items-center gap-2 bg-white text-indigo-600 px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-0.5"
            >
              Start Creating Free
              <ArrowRight className="w-5 h-5" />
            </div>

            <Link
              to="/templates"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300"
            >
              <Play className="w-5 h-5" />
              Watch Demo
            </Link>
          </div>

          <div className="mt-10 flex items-center justify-center gap-8 text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              No credit card required
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Free 3 videos/month
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Cancel anytime
            </div>
          </div>
        </div>
      </section>

      <AuthPopup
        authMode={authMode}
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
      />

      <Footer />

      {/* Global Styles */}
      <style jsx global>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes floatDelayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: floatDelayed 3.5s ease-in-out infinite;
        }
        
        .reveal-text {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        
        .reveal-text.active {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}

// ================= SUB-COMPONENTS =================

function Badge({ icon, text }) {
  return (
    <div className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-full shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-300">
      <span className="text-indigo-500">{icon}</span>
      <span className="text-sm font-medium text-gray-700">{text}</span>
    </div>
  );
}

function FloatingCard({ className, icon, iconBg, title, value, variant = "stacked" }) {
  if (variant === "inline") {
    return (
      <div className={`absolute ${className} bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg border border-gray-200 hidden lg:flex items-center gap-3 animate-float-delayed z-20`}>
        <div className={`w-8 h-8 bg-gradient-to-br ${iconBg} rounded-full flex items-center justify-center`}>
          {icon}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">{value}</span>
          <span className="text-xs text-gray-500">{title}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`absolute ${className} bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-4 hidden lg:block animate-float z-20`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 bg-gradient-to-br ${iconBg} rounded-xl flex items-center justify-center`}>
          {icon}
        </div>
        <div>
          <p className="text-xs text-gray-500">{title}</p>
          <p className="text-lg font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

function PlatformIcon({ color }) {
  return (
    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${color} border-2 border-white shadow-md flex items-center justify-center`}>
      <Users className="w-5 h-5 text-white" />
    </div>
  );
}